import { BASE_Z_REPORTS_QUERY_KEY } from "@/services/queryKeys"
import { registerPlugin } from "./utils/registerPlugin"
import { HandledError } from "@/utils/HandledError"
import { queryClient } from "@/queryClient"
import { getMockXZReport } from "./mockData/IReportsMockData"

export type XZReport = {
  // These may vary by acquirer and payment app
  headerFields?: Record<string, string>

  fromReceiptNo: string | null
  toReceiptNo: string | null

  saleCount: number
  saleAmount: number

  voidCount: number
  voidAmount: number

  cashbackCount: number
  cashbackAmount: number
  gratuityCount: number
  gratuityAmount: number
  refundCount: number
  refundAmount: number

  completionCount: number
  completionAmount: number

  preAuthCount: number
  preAuthAmount: number
  preAuthTopUpCount: number
  preAuthTopUpAmount: number
  preAuthCancelledCount: number
  preAuthCancelledAmount: number

  totalCount: number
  totalAmount: number

  terminalPrintedNativeReceipt: boolean
  // Raw receipt info from pax. Currently only appears for BC terminals and contains the "Z-report".
  // If this is present prefer printing this to our own receipt
  nativeReceiptLines: Array<string>
  // Raw json of report payload from payment app
  payloadJson: string
}

type IReportsPlugin = {
  printZReport: (config: {
    isAmex: boolean
  }) => Promise<void>
  printXReport: () => Promise<void>
  generateZReport: (config: {
    isAmex: boolean
  }) => Promise<XZReport>
  generateXReport: () => Promise<XZReport>
  getReportMerchantId: () => Promise<{ value: string }>
}

const rawIReports = registerPlugin<IReportsPlugin>(
  "IReportsPlugin",
  {
    printXReport: () => {
      throw new Error(
        "X reports not supported on web platform"
      )
    },
    printZReport: () => {
      throw new Error(
        "Z reports not supported on web platform"
      )
    },
    generateXReport: async () =>
      Promise.resolve(getMockXZReport()),
    generateZReport: async () =>
      Promise.resolve(getMockXZReport()),
    getReportMerchantId: async () =>
      Promise.resolve({ value: "1234" }),
  }
)

export const IReports: IReportsPlugin = {
  printXReport: rawIReports.printXReport,

  printZReport: async (config) => {
    try {
      await rawIReports.printZReport(config)
      queryClient.invalidateQueries([
        BASE_Z_REPORTS_QUERY_KEY,
      ])
    } catch (error) {
      console.error("Failed to print Z Report:", error)
      throw error
    }
  },

  generateZReport: async (config) => {
    try {
      const result =
        await rawIReports.generateZReport(config)
      queryClient.invalidateQueries([
        BASE_Z_REPORTS_QUERY_KEY,
      ])
      return result
    } catch (error) {
      const errorMessage = (error as Error).message

      switch (errorMessage) {
        case "No transactions to reconcile":
          toast.error("No transactions to reconcile")
          throw new HandledError(errorMessage)
        case "Amex not enabled":
          toast.error("Amex not enabled")
          throw new HandledError(errorMessage)
        case "No response from positive":
          toast.error(
            "Unable to launch payment application"
          )
          throw new HandledError(errorMessage)
        case "EvoPayments application is busy":
          toast.error(
            "EvoPayments is busy. Exit TP+ and close EvoPayments to make payments.",
            10000
          )
          throw new HandledError(errorMessage)
      }

      throw error
    }
  },
  generateXReport: rawIReports.generateXReport,
  getReportMerchantId: rawIReports.getReportMerchantId,
}
